import {
  Modal,
  FormLayout,
  TextField,
  BlockStack,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import useFormattedCurrency from "@shared/hooks/common/useFormattedCurrency";

interface AddCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  minPurchaseAmount: number;
  maxPurchaseAmount: number;
  currency: string;
  clinicId: string;
}

export function AddCreditsModal({ isOpen, onClose, minPurchaseAmount, maxPurchaseAmount, currency, clinicId }: AddCreditsModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { POST } = useApiRequest();

  const [amount, setAmount] = useState("5"); // Default to minimum amount
  const [errors, setErrors] = useState<{ amount?: string }>({});

  // Use the formatted currency hook for min/max amounts
  const { formattedValue: formattedMinAmount } = useFormattedCurrency({
    value: minPurchaseAmount.toString(),
    currencyIso: currency,
    decimalPlaces: 0,
  });
  const { formattedValue: formattedMaxAmount } = useFormattedCurrency({
    value: maxPurchaseAmount.toString(),
    currencyIso: currency,
    decimalPlaces: 0,
  });


  // Mutation for purchasing credits
  type PurchaseCreditsResponse = { link?: string };
  const purchaseCreditsMutation = useMutation({
    mutationFn: (data: { amount: number }) => {
      return POST<PurchaseCreditsResponse>(
        `/api/healthcare-companies/${clinicId}/billing/credits`,
        {
          amount: data.amount,
          redirectURI: window.location.href,
        }
      );
    },
    onSuccess: (response) => {
      // The API might return a redirect link for payment
      if (response?.link) {
        window.location.href = response?.link;
      }
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ 
        queryKey: ["subscription", clinicId] 
      });
      onClose();
    },
    onError: (error: unknown) => {
      console.error("Failed to purchase credits:", error);
    },
  });

  const validateAmount = (value: string) => {
    const numValue = parseFloat(value);
    const newErrors: { amount?: string } = {};

    if (!value || isNaN(numValue)) {
      newErrors.amount = t("settings.subscription.credits.errors.invalidAmount", {
        defaultValue: "Please enter a valid amount"
      });
    } else if (numValue < minPurchaseAmount) {
      newErrors.amount = t("settings.subscription.credits.errors.minAmount", {
        defaultValue: `Minimum amount is ${currency}${minPurchaseAmount}`
      });
    } else if (numValue > maxPurchaseAmount) {
      newErrors.amount = t("settings.subscription.credits.errors.maxAmount", {
        defaultValue: `Maximum amount is ${currency}${maxPurchaseAmount}`
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAmountChange = (value: string) => {
    setAmount(value);
    if (errors.amount) {
      validateAmount(value);
    }
  };

  const handleSubmit = () => {
    if (validateAmount(amount)) {
      purchaseCreditsMutation.mutate({
        amount: parseFloat(amount),
      });
    }
  };

  const handleClose = () => {
    setAmount("5");
    setErrors({});
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={t("settings.subscription.credits.modal.title", { defaultValue: "Add credit to balance" })}
      primaryAction={{
        content: t("settings.subscription.credits.modal.continue", { defaultValue: "Continue" }),
        onAction: handleSubmit,
        loading: purchaseCreditsMutation.isPending,
        disabled: !!errors.amount || !amount,
      }}
      secondaryActions={[
        {
          content: t("common.cancel", { defaultValue: "Cancel" }),
          onAction: handleClose,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">

          <FormLayout>
            <FormLayout.Group>
              <TextField
                label={t("settings.subscription.credits.modal.amount", { defaultValue: "Amount to add" })}
                value={amount}
                onChange={handleAmountChange}
                onBlur={() => validateAmount(amount)}
                error={errors.amount}
                prefix={currency}
                type="number"
                min={minPurchaseAmount}
                max={maxPurchaseAmount}
                autoComplete="off"
                helpText={t("settings.subscription.credits.modal.amountHelp", {
                  defaultValue: `Enter an amount between ${formattedMinAmount} and ${formattedMaxAmount}`
                })}
              />
            </FormLayout.Group>
          </FormLayout>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
} 
